import React, { FC, ReactNode, useContext, useMemo } from 'react';
import { ThemeContext } from 'styled-components';

import { WithSpacingProps } from 'ia-react-core/~/hocs/grid/withSpacing';
import { SCP } from 'ia-react-core/~/interfaces/SCP';
import { ReactComponent as PinIcon } from './assets/pin.svg';
import COLOR_SCHEMES from '../../../constants/COLOR_SCHEMES';
import ColorSchemeContext from '../../colorScheme/ColorSchemeContext';
import BlockGroupContext from '../#BlockGroupContext';
import { StyledBlock, StyledPin } from './Block.styles';

export interface BlockProps extends WithSpacingProps, SCP<'section'> {
  /** The block's content */
  children?: ReactNode;
  /** Color scheme of the block. Only use if you change the background shade. */
  colorScheme?: 'light' | 'shade' | 'dark';
  /** Style the Block in white */
  white?: boolean;
  /** Style the Block in primary color */
  primary?: boolean;
  /** Styled the block as a nested block. */
  nested?: boolean;
  /** Overrides the padding of the block. */
  padding?: string;
  /** Overrides the padding of the medium breakpoint of the block. */
  mdPadding?: string;
}

/**
 * @since 0.18.0
 */
const Block: FC<BlockProps> = ({ children, colorScheme, white, primary, nested, padding, mdPadding, ...props }) => {
  const theme = useContext(ThemeContext);
  const { grouped } = useContext(BlockGroupContext) || {};

  const resolvedWhite = useMemo(() => (
    (typeof white !== 'undefined') ? white : theme?.Block?.white
  ), [theme, white]);
  const resolvedPrimary = useMemo(() => (
    (typeof primary !== 'undefined') ? primary : theme?.Block?.primary
  ), [primary, theme]);

  const scheme = useMemo(() => {
    if (colorScheme) {
      return colorScheme;
    }

    if (resolvedWhite) {
      return COLOR_SCHEMES.LIGHT;
    }

    if (resolvedPrimary) {
      return COLOR_SCHEMES.DARK;
    }

    return COLOR_SCHEMES.SHADE;
  }, [colorScheme, resolvedPrimary, resolvedWhite]);
  const colorSchemeContext = useMemo(() => ({ scheme }), [scheme]);

  const blockGroupContext = useMemo(() => ({ grouped: false }), []);

  return (
    <ColorSchemeContext.Provider value={colorSchemeContext}>
      <StyledBlock<any>
        grouped={grouped}
        as={grouped ? 'div' : undefined}
        white={resolvedWhite}
        primary={resolvedPrimary}
        nested={nested}
        padding={padding}
        mdPadding={mdPadding}
        {...props}
      >
        {nested && <StyledPin as={PinIcon} />}
        <BlockGroupContext.Provider value={blockGroupContext}>
          {children}
        </BlockGroupContext.Provider>
      </StyledBlock>
    </ColorSchemeContext.Provider>
  );
};

export default Block;
